import React, { useState, useEffect, useMemo } from 'react';
import { Link } from "react-router-dom";
import 'bootstrap/dist/css/bootstrap.min.css';
import { usePlantData } from '../../../components/data/PlantData';
import SidebarPageLayout from '../../../components/layout/SidebarPageLayout';
import Marquee from '../../../components/layout/Marquee';
import Breadcrumbs from '../../../components/layout/Breadcrumbs';
import ChartTooltip from '../../../components/layout/ChartTooltip';
import MonthRangePicker from '../../../components/layout/MonthRangePicker';
import PlantSolarOutputVsIrradiance, {
  toInsightsMonthSlug,
  toIsoMonthStart,
} from '../../../components/graphs/PlantSolarOutputVsIrradiance';
import { toSlug } from '../../../util/strings';
import { formatTemperature } from '../../../util/temperature';
import { useTemperatureUnit } from '../../../util/useTemperatureUnit';
import { apiFetch } from '../../../api/apiClient';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartColumn, faTable, faTableColumns } from '@fortawesome/free-solid-svg-icons';

function formatMwDc(value, fractionDigits = 1) {
  if (value == null || !Number.isFinite(Number(value))) return '—';
  return `${Number(value).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: fractionDigits,
  })} MWdc`;
}

function formatVariance(mw, pct) {
  if (mw == null || !Number.isFinite(Number(mw))) {
    return { text: '—', tone: null };
  }
  const n = Number(mw);
  const signed = `${n > 0 ? '+' : ''}${Math.round(n).toLocaleString('en-US')} MWdc`;
  const tone = n < 0 ? 'negative' : n > 0 ? 'positive' : null;
  if (pct == null || !Number.isFinite(Number(pct))) {
    return { text: signed, tone };
  }
  const signedPct = (n < 0 ? -Math.abs(Number(pct)) : Math.abs(Number(pct))).toFixed(1);
  return { text: `${signed} (${signedPct}%)`, tone };
}

function formatStcIrradiance(value) {
  if (value == null || !Number.isFinite(Number(value))) return '—';
  return `${Number(value).toLocaleString('en-US')} W/m2`;
}

function formatStcTemp(value, unit) {
  return formatTemperature(value, unit, { from: 'celsius' });
}

function formatExcelNumber(value) {
  if (value == null || !Number.isFinite(Number(value))) return '';
  const n = Number(value);
  const abs = Math.abs(n);
  if (abs !== 0 && abs < 0.0005) {
    return n.toExponential(3);
  }
  const rounded = Math.round(n * 1000) / 1000;
  if (Number.isInteger(rounded)) return String(rounded);
  return rounded.toFixed(3);
}

function AnovaCell({ value }) {
  return <td>{value == null || value === '' ? '\u00a0' : formatExcelNumber(value)}</td>;
}

function CoefficientRow({ row }) {
  const lower = row?.lower95;
  const upper = row?.upper95;
  return (
    <tr>
      <td><strong>{row?.name}</strong></td>
      <td>{formatExcelNumber(row?.coefficient)}</td>
      <td>{formatExcelNumber(row?.standardError)}</td>
      <td>{formatExcelNumber(row?.tStat)}</td>
      <td>{formatExcelNumber(row?.pValue)}</td>
      <td>{formatExcelNumber(lower)}</td>
      <td>{formatExcelNumber(upper)}</td>
      <td>{formatExcelNumber(lower)}</td>
      <td>{formatExcelNumber(upper)}</td>
    </tr>
  );
}

function DataQuality() {
  const temperatureUnit = useTemperatureUnit();
  const { plant: gemini } = usePlantData();
  const portfolioName = useMemo(() => toSlug(gemini.plant_name) || 'gemini', [gemini.plant_name]);
  const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, content: '' });
  const [chartRange, setChartRange] = useState(() => {
    const now = new Date();
    return {
      start: new Date(now.getFullYear(), now.getMonth() - 12, 1),
      end: new Date(now.getFullYear(), now.getMonth(), 1),
    };
  });
  const [chartView, setChartView] = useState(true);
  const [regression, setRegression] = useState(null);
  const [regressionLoading, setRegressionLoading] = useState(false);
  const [regressionError, setRegressionError] = useState(null);
  const toggleChartView = () => {
    setChartView((prev) => !prev);
  };

  const startMonth = toIsoMonthStart(chartRange.start);
  const endMonth = toIsoMonthStart(chartRange.end) || startMonth;
  const duration = toInsightsMonthSlug(chartRange.end) || toInsightsMonthSlug(chartRange.start) || 'june-2026';

  useEffect(() => {
    document.title = "Vivosol Plant Performance Insights Hub | Gemini / Insights / PV USA Model";
  }, []);

  useEffect(() => {
    if (!portfolioName || !startMonth || !endMonth) return undefined;
    let cancelled = false;
    setRegressionLoading(true);
    setRegressionError(null);

    const params = new URLSearchParams({ startMonth, endMonth });
    const path = `/api/graph/portfolio/${portfolioName}/insights/${encodeURIComponent(duration)}/pvusa-regression?${params}`;

    (async () => {
      try {
        const payload = await apiFetch(path);
        if (cancelled) return;
        setRegression(payload);
      } catch (err) {
        if (cancelled) return;
        setRegression(null);
        setRegressionError(err?.message || 'Failed to load PVUSA regression');
      } finally {
        if (!cancelled) setRegressionLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [portfolioName, duration, startMonth, endMonth]);

  return (
    <SidebarPageLayout>
      <div className="content p-5">
        <ChartTooltip tooltip={tooltip} transition={false} />
        <Marquee />
        <h1 className="mt-3 mb-0"><Link to={`${gemini.dashboard}`}><strong>{gemini.plant_name}</strong></Link> : {gemini.config} ({gemini.location})</h1>
        <Breadcrumbs labels={{ portfolioName: gemini.plant_name }} />
        <div id="pvUSAModel" className="panel p-3 mb-5">
          <div className="header d-flex justify-content-between pb-3 mb-3">
            <div className="left d-flex gap-3 align-items-center">
              <span><FontAwesomeIcon icon={faChartColumn} />{chartView ? 'SOLAR PERFORMANCE' : 'SOLAR PERFORMANCE - OUTPUT SUMMARY'}</span>
              <MonthRangePicker range={chartRange} setRange={setChartRange} />
            </div>
            <div className="right d-flex align-items-stretch gap-2">
              <button type="button" onClick={toggleChartView}>
                <FontAwesomeIcon icon={chartView ? faTableColumns : faTable} />
              </button>
            </div>
          </div>
          <div className={`d-flex align-items-center gap-5 ${chartView ? '' : 'd-none'}`}>
            <div className="col-8">
              <PlantSolarOutputVsIrradiance
                portfolioName={portfolioName}
                range={chartRange}
                onTooltip={setTooltip}
              />
            </div>
            <div className="col">
              <strong>SUMMARY OUTPUT</strong>
              {regressionLoading ? (
                <div className="text-muted small mt-2">Updating summary…</div>
              ) : regressionError ? (
                <div className="alert alert-danger py-2 mt-2 mb-0" role="alert">{regressionError}</div>
              ) : !regression?.summary ? (
                <div className="text-muted small mt-2">Summary appears after the PVUSA fit completes for this range.</div>
              ) : (
                <>
                  <div className="d-flex flex-row align-items-center gap-2 justify-content-between">
                    <strong>PV USA Plant Rating (STC):</strong>
                    {formatMwDc(regression.summary.pvusa_plant_rating_stc_mw, 2)}
                  </div>
                  <div className="d-flex flex-row align-items-center gap-2 justify-content-between">
                    <strong>Official Solar DC Rating:</strong>
                    {formatMwDc(regression.summary.expected_solar_dc_capacity_mw, 1)}
                  </div>
                  <div className="d-flex flex-row align-items-center gap-2 justify-content-between">
                    <strong>Variance:</strong>
                    {(() => {
                      const variance = formatVariance(
                        regression.summary.variance_mw,
                        regression.summary.variance_pct,
                      );
                      return (
                        <span className={variance.tone ? `variance-value ${variance.tone}` : undefined}>
                          {variance.text}
                        </span>
                      );
                    })()}
                  </div>
                </>
              )}
              <br/>
              <strong>STANDARD TEST CONDITIONS</strong>
              <div className="d-flex flex-row align-items-center gap-2 justify-content-between">
                <strong>Irradiance:</strong>  {formatStcIrradiance(regression?.summary?.stc?.irradiance_wpm2 ?? 1000)}
              </div>
              <div className="d-flex flex-row align-items-center gap-2 justify-content-between">
                <strong>BOM Temperature:</strong>  {formatStcTemp(regression?.summary?.stc?.bom_temp_c ?? 25, temperatureUnit)}
              </div>
            </div>
          </div>
          <div className={`pv-usa-table-view ${chartView ? 'd-none' : ''}`}>
            {regressionLoading ? (
              <p className="text-muted mb-0">Loading regression for {startMonth} → {endMonth}…</p>
            ) : regressionError ? (
              <div className="alert alert-danger py-2 mb-0" role="alert">{regressionError}</div>
            ) : !regression?.regression ? (
              <p className="text-muted mb-0">
                Not enough hourly PVUSA samples in this date range to fit the model
                {regression?.observation_count != null ? ` (${regression.observation_count} observations).` : '.'}
              </p>
            ) : (
              <>
            <div className="d-flex flex-row gap-5">
              <div className="col-2 pv-usa-regression-stats">
                <strong className="pv-usa-regression-stats__title">REGRESSION STATISTICS</strong>
                <div className="pv-usa-regression-stats__rows">
                <div className="d-flex flex-row align-items-center gap-2 justify-content-between">
                  <strong>Multiple R:</strong>  {formatExcelNumber(regression.regression.multipleR)}
                </div>
                <div className="d-flex flex-row align-items-center gap-2 justify-content-between">
                  <strong>R Square:</strong>  {formatExcelNumber(regression.regression.rSquare)}
                </div>
                <div className="d-flex flex-row align-items-center gap-2 justify-content-between">
                  <strong>Adjusted R Square:</strong>  {formatExcelNumber(regression.regression.adjustedRSquare)}
                </div>
                <div className="d-flex flex-row align-items-center gap-2 justify-content-between">
                  <strong>Standard Error:</strong>  {formatExcelNumber(regression.regression.standardError)}
                </div>
                <div className="d-flex flex-row align-items-center gap-2 justify-content-between">
                  <strong>Observations:</strong>  {formatExcelNumber(regression.regression.observations)}
                </div>
                </div>
              </div>
              <div className="col">
                <strong>ANOVA</strong>
                <table className="table table-bordered">
                  <thead>
                    <tr>
                      <th>&nbsp;</th>
                      <th>df</th>
                      <th>SS</th>
                      <th>MS</th>
                      <th>F</th>
                      <th>Significance F</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td><strong>Regression</strong></td>
                      <AnovaCell value={regression.anova.regression.df} />
                      <AnovaCell value={regression.anova.regression.ss} />
                      <AnovaCell value={regression.anova.regression.ms} />
                      <AnovaCell value={regression.anova.regression.f} />
                      <AnovaCell value={regression.anova.regression.significanceF} />
                    </tr>
                    <tr>
                      <td><strong>Residual</strong></td>
                      <AnovaCell value={regression.anova.residual.df} />
                      <AnovaCell value={regression.anova.residual.ss} />
                      <AnovaCell value={regression.anova.residual.ms} />
                      <td>&nbsp;</td>
                      <td>&nbsp;</td>
                    </tr>
                    <tr>
                      <td><strong>Total</strong></td>
                      <AnovaCell value={regression.anova.total.df} />
                      <AnovaCell value={regression.anova.total.ss} />
                      <td>&nbsp;</td>
                      <td>&nbsp;</td>
                      <td>&nbsp;</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <hr />
            <div className="col">
              <table className="table table-bordered">
                  <thead>
                    <tr>
                      <th>&nbsp;</th>
                      <th>Coefficients</th>
                      <th>Standard Error</th>
                      <th>t Stat</th>
                      <th>P-value</th>
                      <th>Lower 95%</th>
                      <th>Upper 95%</th>
                      <th>Lower 95.0%</th>
                      <th>Upper 95.0%</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(regression.coefficients || []).map((row) => (
                      <CoefficientRow key={row.name} row={row} />
                    ))}
                  </tbody>
                </table>
            </div>
              </>
            )}
          </div>
        </div>
      </div>
    </SidebarPageLayout>
  );
}

export default DataQuality;
